import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import api, { clearApiCache, setActingAsChild } from '../services/api';

interface User {
  id: string;
  email: string;
  profileType: string;
  firstName: string;
  lastName: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => void;
  resetPassword: (email: string) => Promise<void>;
  loading: boolean;
  actingAs: User | null;
  switchToChild: (childId: string) => Promise<void>;
  switchBackToParent: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actingAs, setActingAs] = useState<User | null>(null);
  const [originalUser, setOriginalUser] = useState<User | null>(null);

  // Set up axios auth header whenever token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Listen for Supabase auth state changes
  useEffect(() => {
    // Check for existing session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setToken(session.access_token);
        // Restore user from localStorage while we fetch fresh data
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
        // Fetch fresh profile data
        fetchProfile(session.access_token);
      }
      setLoading(false);
    });

    // Subscribe to auth changes (token refresh, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setToken(session.access_token);
      } else {
        setToken(null);
        setUser(null);
        localStorage.removeItem('user');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchProfile = async (accessToken: string) => {
    try {
      const response = await api.get('/users/me', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const userData = response.data;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  };

  const login = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      throw new Error(error.message);
    }

    const accessToken = data.session.access_token;
    setToken(accessToken);

    // Fetch profile from our Express API
    const response = await api.get('/users/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const userData = response.data;
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const register = async (userData: any) => {
    const { data, error } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          profileType: userData.profileType,
        },
      },
    });

    if (error) {
      throw new Error(error.message);
    }

    if (!data.session) {
      throw new Error('Registration succeeded but no session returned. Check email confirmation settings.');
    }

    const accessToken = data.session.access_token;
    setToken(accessToken);

    // Update profile with additional fields via our Express API
    try {
      await api.put('/users/me', {
        profilePicture: userData.profilePicture,
        dateOfBirth: userData.dateOfBirth,
        phoneNumber: userData.phoneNumber,
        address: userData.address,
        soccerExperience: userData.soccerExperience,
      }, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch (err) {
      console.error('Failed to update profile after registration:', err);
    }

    // Fetch the complete profile
    const response = await api.get('/users/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const newUser = response.data;
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const logout = async () => {
    await supabase.auth.signOut();
    clearApiCache();
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) {
      throw new Error(error.message);
    }
  };

  const switchToChild = async (childId: string) => {
    try {
      const response = await api.get(`/parent-child/${childId}/context`, {
        headers: { 'X-Acting-As': childId },
      });
      const childProfile = response.data.profile;
      const childUser: User = {
        id: childProfile.id,
        email: childProfile.email,
        profileType: childProfile.profileType,
        firstName: childProfile.firstName,
        lastName: childProfile.lastName,
      };
      setOriginalUser(user);
      setActingAs(childUser);
      setUser(childUser);
      setActingAsChild(childId);
    } catch (err) {
      console.error('Failed to switch to child:', err);
      throw err;
    }
  };

  const switchBackToParent = () => {
    if (originalUser) {
      setUser(originalUser);
      setActingAs(null);
      setOriginalUser(null);
      setActingAsChild(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, resetPassword, loading, actingAs, switchToChild, switchBackToParent }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
